import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Dumbbell, CheckCircle, LogIn, LogOut, Phone, Clock, QrCode, User } from "lucide-react";
import { PhoneInput } from "react-international-phone";
import { isValidPhoneNumber } from "libphonenumber-js";
import "react-international-phone/style.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicAttendance() {
  const { orgId, branchId } = useParams();
  const [orgInfo, setOrgInfo] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/org/${orgId}`).then((r) => setOrgInfo(r.data.organization)).catch(console.error);
    axios.get(`${API}/public/branch/${orgId}/${branchId}`).then((r) => setBranchInfo(r.data.branch)).catch(() => { });
  }, [orgId, branchId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone) { toast.error("Enter your phone number"); return; }
    if (!isValidPhoneNumber(phone)) { toast.error("Enter a valid phone number"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/attendance/public-checkin`, { phone, organizationId: orgId, branchId });
      setResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Check-in failed");
      setResult(null);
    }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setResult(null);
    setPhone("");
  };

  const currentTime = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const currentDate = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const signupUrl = `${window.location.origin}/public/signup/${orgId}/${branchId}`;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-card border-b border-border p-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Dumbbell className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-lg font-bold font-heading text-primary">{orgInfo?.name || "Gym"}</h1>
              {branchInfo && <p className="text-xs text-muted-foreground">{branchInfo.name}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-mono font-bold">{currentTime}</p>
            <p className="text-xs text-muted-foreground">{currentDate}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {result ? (
            <Card className="bg-card border-border rounded-sm">
              <CardContent className="p-8 text-center">
                <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6 ${result.action === "checkin" ? "bg-green-500/20" : "bg-blue-500/20"}`}>
                  {result.action === "checkin" ? <LogIn className="h-10 w-10 text-green-400" /> : <LogOut className="h-10 w-10 text-blue-400" />}
                </div>
                <Avatar className="h-16 w-16 mx-auto mb-4">
                  <AvatarFallback className="bg-primary/20 text-primary text-xl font-heading">
                    {result.memberName?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-2xl font-bold font-heading">{result.memberName}</h3>
                <Badge className={`mt-3 ${result.action === "checkin" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"} border-0 rounded-sm`}>
                  {result.action === "checkin" ? "Checked In" : "Checked Out"}
                </Badge>
                <p className="text-lg mt-4 text-muted-foreground">{result.message}</p>
                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {currentTime}
                </div>
                <Button variant="outline" className="rounded-sm mt-6 w-full" onClick={resetForm} data-testid="scan-again-btn">
                  <QrCode className="h-4 w-4 mr-2" /> Next Member
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-heading text-xl">Attendance</CardTitle>
                <CardDescription>Check in or out using your phone number</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-base">Phone Number</Label>
                    <PhoneInput
                      defaultCountry="in"
                      value={phone}
                      onChange={setPhone}
                      disabled={loading}
                      inputClassName="flex h-12 w-full rounded-sm border border-input bg-secondary px-3 text-lg  focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary/40 bg-transparent outline-none flex-1 h-full"
                      className="w-full mt-2"
                      data-testid="attendance-phone"
                    />
                  </div>
                  <Button type="submit" className="w-full rounded-sm h-12 text-base" disabled={loading} data-testid="attendance-submit-btn">
                    {loading ? "Processing..." : "Check In / Out"}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-center text-sm text-muted-foreground mb-3">Not a member yet?</p>
                  <Button variant="outline" className="w-full rounded-sm" asChild data-testid="signup-link-btn">
                    <a href={signupUrl}>
                      <User className="h-4 w-4 mr-2" /> Register as New Member
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 text-center">
        <p className="text-xs text-muted-foreground">Powered by Optimus Gym CRM</p>
      </div>
    </div>
  );
}
